import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import aivyLogo from "../../images/aivy_logo3.png";
import { useIsAuthenticated } from "../../store/authStore";
import { useSignOut } from "../../hooks/auth/useSignOut";

const Header = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isLoggedIn = useIsAuthenticated();

  const { signOut } = useSignOut();

  const navigationItems = [
    { label: "AI Studio", href: "https://aistudio.aivy.kr", sectionId: "studio", isExternal: true },
    { label: "Agent Gallery", href: "/agent-list", sectionId: "agent-list" },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignIn = () => {
    navigate("/auth/signin");
  };

  const handleSignOut = () => {
    signOut();
  };

  const handleNavClick = (item: { href: string; sectionId: string; isExternal?: boolean }) => {
    if (item.isExternal) {
      window.open(item.href, '_blank');
    } else {
      navigate(item.href);
    }
  };

  return (
    <header className="bg-gray-50 border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-[1640px] mx-auto px-0 md:px-10">
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img
                src={aivyLogo}
                alt="Aivy Logo"
                className="h-12 md:h-14 w-auto mr-2"
              />
              <span className="text-xl md:text-2xl font-semibold text-gray-900 font-serif-en">Aivy</span>
            </Link>
          </div>

          <div>
            <nav className="hidden md:flex items-center space-x-8 font-serif-en">
              {navigationItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className="text-gray-700 hover:text-gray-900 text-lg font-semibold transition-colors duration-200 py-2 flex items-center h-full"
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="hidden md:flex items-center font-serif-kr">
            {!isLoggedIn ? (
              <div className="flex items-center space-x-8">
                <button
                  onClick={() => navigate("/cs")}
                  className="text-gray-700 hover:text-gray-900 text-sm md:text-base font-semibold transition-colors duration-200"
                >
                  고객센터
                </button>
                <button
                  onClick={handleSignIn}
                  className="bg-black text-white px-6 py-2 rounded-lg text-sm md:text-base font-semibold hover:bg-gray-800 transition-colors duration-200"
                >
                  시작하기
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-8">
                <button
                  onClick={() => navigate("/mypage")}
                  className="text-gray-700 hover:text-gray-900 text-sm md:text-base font-semibold transition-colors duration-200"
                >
                  마이페이지
                </button>
                <button
                  onClick={() => navigate("/cs")}
                  className="text-gray-700 hover:text-gray-900 text-sm md:text-base font-semibold transition-colors duration-200"
                >
                  고객센터
                </button>
                <button
                  onClick={handleSignOut}
                  className="bg-black text-white px-6 py-2 rounded-lg text-sm md:text-base font-semibold hover:bg-gray-800 transition-colors duration-200 font-serif-kr"
                >
                  로그아웃
                </button>
              </div>
            )}
          </div>

          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="bg-gray-50 rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            >
              <span className="sr-only">
                메뉴 {isMobileMenuOpen ? "닫기" : "열기"}
              </span>
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* 모바일 메뉴 */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200">
              {navigationItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    handleNavClick(item);
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm md:text-base font-semibold w-full text-left transition-colors duration-200 flex items-center"
                >
                  {item.label}
                </button>
              ))}

              {isLoggedIn && (
                <>
                  <button
                    onClick={() => navigate("/mypage")}
                    className="text-gray-700 hover:text-gray-900 block px-3 py-2 text-sm md:text-base font-semibold w-full text-left transition-colors duration-200"
                  >
                    마이페이지
                  </button>
                </>
              )}
              
              <button
                onClick={() => navigate("/cs")}
                className="text-gray-700 hover:text-gray-900 block px-3 py-2 text-sm md:text-base font-semibold w-full text-left transition-colors duration-200"
              >
                고객센터
              </button>

              <div className="pt-3">
                {!isLoggedIn ? (
                  <button
                    onClick={handleSignIn}
                    className="w-full bg-black text-white px-6 py-3 rounded-lg text-sm md:text-base font-semibold hover:bg-gray-800 transition-colors duration-200"
                  >
                    시작하기
                  </button>
                ) : (
                  <button
                    onClick={handleSignOut}
                    className="w-full bg-black text-white px-6 py-3 rounded-lg text-sm md:text-base font-semibold hover:bg-gray-800 transition-colors duration-200"
                  >
                    로그아웃
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
